//@ts-check

/**
 * @fileoverview useTsepEventHandler - Custom hook for TSEP event handling
 *
 * @description
 * Extracts TSEP (Multipass gateway) event handling logic from PaymentForm component.
 * Handles TokenEvent, FieldValidationErrorEvent, and FocusOutEvent using object mapper pattern.
 */

import { useEffect } from 'react';
import commonStrings from '../../../constants/commonStrings';

/**
 * @description Custom hook for handling TSEP events from Multipass gateway
 * @param {TsepEventHandlerDependencies} deps - Dependencies for event handling
 * @returns {void}
 */
export const useTsepEventHandler = (deps) => {
  const { paymentGateway, setTsepResponse, setTsepFieldSuccess, setTsepFieldError, checkCardPaymentType } = deps;

  /**
   * @description TSEP event listener for Multipass gateway - following VirtualTerminal pattern
   * Handles TokenEvent, FieldValidationErrorEvent, and FocusOutEvent using object mapper pattern
   * @param {Event & { detail: TsepEvent }} event - TSEP custom event
   */
  const handleTsepEventUpdated = (event) => {
    const eventTsep = event.detail;
    const { eventType, event: tsepPayload } = eventTsep;

    /** @type {Record<string, (e: any) => void>} */
    const tsepMapper = {
      FieldValidationErrorEvent: (e) => {
        setTsepFieldError?.(e?.message);
        setTsepFieldSuccess?.(false);
      },
      TokenEvent: (e) => {
        if (e?.status === 'PASS') {
          // Store encrypted data in localStorage
          if (e.tsepToken && e.cvv2) {
            localStorage.setItem('0', e.tsepToken);
            localStorage.setItem('1', e.cvv2);
          }
          // Store complete TSEP response locally
          setTsepResponse({ ...e });
          setTsepFieldSuccess?.(true);
          setTsepFieldError?.(null);

          // Trigger BIN lookup to capture card type and details
          if (e?.cardNum) {
            const cardNumber = e.cardNum.replaceAll(' ', '');
            if (cardNumber.length >= 6) {
              checkCardPaymentType(cardNumber);
            }
          }
        } else {
          setTsepResponse({ status: 'FAIL', tsepToken: '' });
          setTsepFieldError?.(e?.message || 'A card encryption error has occurred. Please refresh and try again.');
          setTsepFieldSuccess?.(false);
        }
      },
      FocusOutEvent: (e) => {
        if (e?.fieldName === 'tsep-cardNum') {
          const cardNumElement = document.getElementById('tsep-cardNum');
          if (cardNumElement) {
            // @ts-ignore - DOM input element has value property
            checkCardPaymentType(cardNumElement.value.replaceAll(' ', ''));
          }
        }
      },
    };

    if (tsepMapper[eventType]) {
      tsepMapper[eventType](tsepPayload);
    }
  };

  useEffect(() => {
    if (paymentGateway === commonStrings.multipass) {
      // @ts-ignore - Custom event 'tsepEventUpdate' not in DocumentEventMap
      document.addEventListener('tsepEventUpdate', handleTsepEventUpdated);
    }

    return () => {
      // @ts-ignore - Custom event 'tsepEventUpdate' not in DocumentEventMap
      document.removeEventListener('tsepEventUpdate', handleTsepEventUpdated);
    };
  }, [paymentGateway]);
};

export default useTsepEventHandler;

/** @typedef {{ tsepToken?: string, cvv2?: string, expirationDate?: string, cardType?: string, status?: string, cardNum?: string, message?: string }} TsepTokenEvent */
/** @typedef {{ message?: string, fieldName?: string }} TsepFieldValidationErrorEvent */
/** @typedef {{ fieldName?: string }} TsepFocusOutEvent */
/** @typedef {{ eventType: string, event: TsepTokenEvent | TsepFieldValidationErrorEvent | TsepFocusOutEvent }} TsepEvent */

/**
 * @typedef {Object} TsepEventHandlerDependencies
 * @property {string} paymentGateway - Current payment gateway
 * @property {Function} setTsepResponse - Set TSEP response state
 * @property {Function|null} setTsepFieldSuccess - Set TSEP field success state
 * @property {Function|null} setTsepFieldError - Set TSEP field error state
 * @property {Function} checkCardPaymentType - Function to check card payment type via BIN lookup
 */
